import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { LibraryRecentSearchScope } from "common/interfaces";
import { Button as BAIButton } from "js/Components/Button";
import { Icon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { AiIcon } from "js/editor/AiGeneration/AiIcon";
import {
    filterRecentSearches,
    RecentSearchesDropdown,
} from "js/editor/PresentationLibrary/components/RecentSearchesDropdown";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { themeColors } from "js/react/sharedStyles";
import { FilterDropDown } from "js/react/views/AddSlide/Panes/Components/FilterDropDown";
import { SearchBar } from "js/react/views/AddSlide/Panes/Components/SearchBox";

export interface SearchAndAddFilterOption {
    field: string;
    name: string;
    count?: number;
    isPinned?: boolean;
}

export interface SearchAndAddAction {
    id?: string;
    label: string;
    onClick: () => void;
    variant: "ai" | "primary" | "upgrade";
}

export interface SearchAndAddProps {
    actions?: SearchAndAddAction[];
    className?: string;
    contentFilterOptions?: SearchAndAddFilterOption[];
    onSearch: (value: string) => void;
    onContentFilterChange?: (event: { target: { value: string } }) => void;
    onTagFilterChange?: (event: { target: { value: string } }) => void;
    searchPlaceholder?: string;
    searchValue?: string;
    selectedContentFilter?: string;
    selectedTagFilter?: string;
    tagFilterOptions?: SearchAndAddFilterOption[];
    selectedFolderName?: string | null;
    searchInFolder?: boolean;
    onSearchInFolderChange?: (enabled: boolean) => void;
    recentSearchScope: LibraryRecentSearchScope;
}

const Row = styled.div`
    width: 100%;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 16px;
`;

const SearchFrame = styled.div`
    position: relative;
    flex: 1;
    display: flex;
    align-items: center;
    background: white;
    box-sizing: border-box;
`;

const StyledSearchBar = styled(SearchBar)`
    flex: 1;
    min-width: 0;
    margin: 0;
    padding: 6px 10px;
    gap: 10px;
    border: none;
    background: transparent;
    box-sizing: border-box;

    .MuiIcon-root {
        color: ${themeColors.ui_blue};
    }

    input[type="text"] {
        width: 100%;
        margin: 0;
        font-size: 16px;
        font-weight: 500;
        padding-right: 24px;
    }

    input[type="text"]::placeholder {
        color: #999;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-weight: 400;
        line-height: 150%;
    }
`;

const Actions = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    margin-left: auto;
`;

const StyledBAIButton = styled(BAIButton)`
    padding: 6px 20px;
    border-radius: 3px;
`;

function SearchAndAddActionButton({ action }: { action: SearchAndAddAction }) {
    if (action.variant === "ai") {
        return (
            <StyledBAIButton large ai onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
                <AiIcon />
                {action.label}
            </StyledBAIButton>
        );
    }

    if (action.variant === "upgrade") {
        return (
            <StyledBAIButton large upgrade onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
                <UpgradeIcon />
                {action.label}
            </StyledBAIButton>
        );
    }

    return (
        <StyledBAIButton blue large onClick={action.onClick} id={action.id} style={{ flexShrink: 0 }}>
            <Icon white fill>add_circle</Icon>
            {action.label}
        </StyledBAIButton>
    );
}

export function SearchAndAdd({
    actions = [],
    className,
    contentFilterOptions,
    onSearch,
    onContentFilterChange,
    onTagFilterChange,
    searchPlaceholder = t("Search..."),
    searchValue,
    selectedContentFilter,
    selectedTagFilter,
    tagFilterOptions,
    selectedFolderName,
    searchInFolder = false,
    onSearchInFolderChange,
    recentSearchScope,
}: SearchAndAddProps) {
    const showContentFilter = Boolean(contentFilterOptions && onContentFilterChange && selectedContentFilter != null);
    const showTagFilter = Boolean(tagFilterOptions && onTagFilterChange && selectedTagFilter != null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [activeOptionIndex, setActiveOptionIndex] = useState(-1);
    const [recentSearches, setRecentSearches] = useState(() => app.user.getLibraryRecentSearches(recentSearchScope));

    const persistRecentSearch = (term: string) => {
        if (!term.trim()) {
            return;
        }
        setRecentSearches(app.user.addLibraryRecentSearch(recentSearchScope, term));
    };

    const showSearchInFolder = Boolean(
        selectedFolderName &&
        onSearchInFolderChange &&
        !searchInFolder
    );

    const folderChip = searchInFolder && selectedFolderName
        ? selectedFolderName
        : undefined;

    // Row order matches RecentSearchesDropdown: optional folder row, then filtered recents.
    const matchingRecentSearches = filterRecentSearches(recentSearches, searchValue);
    const dropdownOptionCount = (showSearchInFolder ? 1 : 0) + matchingRecentSearches.length;

    // Typing / chip changes reshuffle options — clear highlight. Do not tie this to recentSearches
    // identity (focus refresh) or ArrowUp/Down loses the selection on the next paint.
    useEffect(() => {
        setActiveOptionIndex(-1);
    }, [searchValue, showSearchInFolder]);

    const closeDropdown = () => {
        setIsDropdownOpen(false);
        setActiveOptionIndex(-1);
    };

    const handleFocus = () => {
        setRecentSearches(app.user.getLibraryRecentSearches(recentSearchScope));
        setActiveOptionIndex(-1);
        setIsDropdownOpen(true);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        // Recent-search rows use mousedown preventDefault so the input does not blur on click.
        // Close on any real blur (filters, chip, outside) so the dropdown cannot stick open.
        // Live search rarely uses Enter — persist on blur so typed queries still land in recents.
        persistRecentSearch(event.target.value);
        closeDropdown();
    };

    const handleSelectRecentSearch = (term: string) => {
        persistRecentSearch(term);
        onSearch(term);
        closeDropdown();
    };

    const handleSearchInSelectedFolder = () => {
        onSearchInFolderChange?.(true);
        closeDropdown();
    };

    const activateOption = (index: number) => {
        if (showSearchInFolder) {
            if (index === 0) {
                handleSearchInSelectedFolder();
                return;
            }
            const term = matchingRecentSearches[index - 1];
            if (term) {
                handleSelectRecentSearch(term);
            }
            return;
        }

        const term = matchingRecentSearches[index];
        if (term) {
            handleSelectRecentSearch(term);
        }
    };

    const moveActiveOption = (isDown: boolean) => {
        const lastIndex = dropdownOptionCount - 1;
        setActiveOptionIndex(currentIndex => {
            if (isDown) {
                // -1 → 0; stop at last (no wrap).
                return currentIndex >= lastIndex ? lastIndex : currentIndex + 1;
            }
            // 0 → -1 (clear highlight); stop at -1 (no wrap to bottom).
            if (currentIndex <= 0) {
                return -1;
            }
            return currentIndex - 1;
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Escape") {
            closeDropdown();
            return;
        }

        if (
            isDropdownOpen &&
            dropdownOptionCount > 0 &&
            (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Tab")
        ) {
            // Tab/arrows move highlight; preventDefault keeps focus in the input (no blur/close).
            event.preventDefault();
            // Read key before setState — synthetic events can be pooled/nullified in the updater.
            const isDown = event.key === "ArrowDown" || (event.key === "Tab" && !event.shiftKey);
            moveActiveOption(isDown);
            return;
        }

        if (event.key === "Enter") {
            if (isDropdownOpen && activeOptionIndex >= 0) {
                event.preventDefault();
                activateOption(activeOptionIndex);
                return;
            }

            persistRecentSearch(event.currentTarget.value);
            closeDropdown();
            return;
        }

        // Backspace at caret start removes folder chip; next typed search goes global.
        if (
            event.key === "Backspace" &&
            folderChip &&
            event.currentTarget.selectionStart === 0 &&
            event.currentTarget.selectionEnd === 0
        ) {
            event.preventDefault();
            onSearchInFolderChange?.(false);
        }
    };

    return (
        <Row className={className}>
            <SearchFrame>
                <StyledSearchBar
                    placeholder={searchPlaceholder}
                    onSearch={onSearch}
                    query={searchValue}
                    chip={folderChip}
                    onRemoveChip={folderChip ? () => onSearchInFolderChange?.(false) : undefined}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                />
                {(showContentFilter || showTagFilter) && (
                    <>
                        {showContentFilter && (
                            <FilterDropDown
                                onChange={onContentFilterChange!}
                                selectedFilter={selectedContentFilter!}
                                filterOptions={contentFilterOptions!}
                                title={t("show matches for")}
                            />
                        )}
                        {showTagFilter && (
                            <FilterDropDown
                                onChange={onTagFilterChange!}
                                selectedFilter={selectedTagFilter!}
                                filterOptions={tagFilterOptions!}
                                title={t("Tags")}
                                searchable
                                searchPlaceholder={t("Search Tags...")}
                            />
                        )}
                    </>
                )}
                {isDropdownOpen && (
                    <RecentSearchesDropdown
                        recentSearches={recentSearches}
                        query={searchValue}
                        showSearchInFolder={showSearchInFolder}
                        activeIndex={activeOptionIndex}
                        onSelectRecentSearch={handleSelectRecentSearch}
                        onSearchInSelectedFolder={handleSearchInSelectedFolder}
                    />
                )}
            </SearchFrame>
            {actions.length > 0 && (
                <Actions>
                    {actions.map(action => (
                        <SearchAndAddActionButton
                            key={`${action.variant}-${action.label}`}
                            action={action}
                        />
                    ))}
                </Actions>
            )}
        </Row>
    );
}
